import MainURL from '../../Admin/MainURL';
import mmWebMain from '../../images/ministermore/web/01_main.jpg';
import mmWebRecruit from '../../images/ministermore/web/02_recruit.jpg';
import mmWebRecruitDetail from '../../images/ministermore/web/20_recruit_detail.jpg';
import mmWebMinister from '../../images/ministermore/web/06_minister.jpg';
import mmWebMinisterDetail from '../../images/ministermore/web/21_minister_detail.jpg';
import mmWebAi from '../../images/ministermore/web/11_aiservice.jpg';
import mmWebAiWorship from '../../images/ministermore/web/12_aiservice_worship.jpg';
import mmWebRetreat from '../../images/ministermore/web/07_retreat.jpg';
import mmWebRetreatCasting from '../../images/ministermore/web/09_retreat_casting.jpg';
import mmWebBoard from '../../images/ministermore/web/13_board.jpg';
import mmAppHome from '../../images/ministermore/app/01_home.jpg';
import mmAppJobs from '../../images/ministermore/app/03_jobs.jpg';
import mmAppJobDetail from '../../images/ministermore/app/04_job_detail.jpg';
import mmAppMinisters from '../../images/ministermore/app/07_ministers.jpg';
import mmAppMinisterDetail from '../../images/ministermore/app/08_minister_detail.jpg';
import mmAppAi from '../../images/ministermore/app/10_ai.jpg';
import mmAppAiSongs from '../../images/ministermore/app/11_ai_songs.jpg';
import mmAppAiConti from '../../images/ministermore/app/12_ai_conti.jpg';
import mmAppRetreat from '../../images/ministermore/app/13_retreat.jpg';
import mmAppRetreatPlace from '../../images/ministermore/app/14_retreat_place_detail.jpg';
import mmAppRetreatCasting from '../../images/ministermore/app/17_retreat_casting.jpg';
import mmAppBoard from '../../images/ministermore/app/18_board.jpg';
import mmAppJobAlert from '../../images/ministermore/app/22_job_alert_form.jpg';

export type ProjectCategory = 'own' | 'outsource';

export interface ProjectScreen {
  imageUrl: string;
  caption: string;
  // 지정하면 상세 페이지에서 웹 화면 / 앱 화면 갤러리로 나눠 보여준다
  kind?: 'web' | 'mobile';
}

export interface ApiProject {
  id: number;
  title: string;
  description: string;
  category: string;
  tech_stacks: string | string[];
  image_url: string;
  project_url?: string;
  web_url?: string;
  android_url?: string;
  ios_url?: string;
  headline?: string;
  client_name?: string;
  industry?: string;
  period?: string;
  scope?: string;
  platform?: string;
  challenge?: string;
  approach?: string;
  result?: string;
  screens?: { image_url: string; caption?: string }[];
  next?: { id: number; title: string } | null;
}

export interface Project {
  id: number;
  title: string;
  description: string;
  category: ProjectCategory;
  thumbnailUrl: string;
  techStack: string[];
  projectUrl: string;
  webUrl: string;
  androidUrl: string;
  iosUrl: string;
  headline: string;
  client: string;
  industry: string;
  period: string;
  scope: string;
  platform: string;
  challenge: string;
  approach: string;
  result: string;
  screens: ProjectScreen[];
}

export const PROJECTS_API_URL = `${MainURL}/api/projects`;

export const CATEGORY_LABELS: Record<ProjectCategory, string> = {
  own: '자체 플랫폼',
  outsource: '외주 프로젝트',
};

const parseTechStacks = (value: string | string[]): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => item.trim()).filter(Boolean);
  }

  return (value || '').split(',').map((item) => item.trim()).filter(Boolean);
};

// 'own'이 아니면(기존 'web'/'app' 값 포함) 외주 프로젝트로 분류
const normalizeCategory = (value: string): ProjectCategory =>
  value === 'own' ? 'own' : 'outsource';

export const normalizeProject = (raw: ApiProject): Project => ({
  id: raw.id,
  title: raw.title,
  description: raw.description,
  category: normalizeCategory(raw.category),
  thumbnailUrl: raw.image_url || '',
  techStack: parseTechStacks(raw.tech_stacks),
  projectUrl: raw.project_url || '',
  // 웹 링크가 비어 있으면 기존 project_url 을 웹 링크로 사용
  webUrl: raw.web_url || raw.project_url || '',
  androidUrl: raw.android_url || '',
  iosUrl: raw.ios_url || '',
  headline: raw.headline || '',
  client: raw.client_name || '',
  industry: raw.industry || '',
  period: raw.period || '',
  scope: raw.scope || '',
  platform: raw.platform || '',
  challenge: raw.challenge || '',
  approach: raw.approach || '',
  result: raw.result || '',
  screens: (raw.screens || [])
    .filter((screen) => screen?.image_url)
    .map((screen) => ({ imageUrl: screen.image_url, caption: screen.caption || '' })),
});

const EMPTY_DETAIL = {
  thumbnailUrl: '',
  challenge: '',
  period: '',
  result: '',
  screens: [],
};

const webScreen = (imageUrl: string, caption: string): ProjectScreen => ({ imageUrl, caption, kind: 'web' });
const appScreen = (imageUrl: string, caption: string): ProjectScreen => ({
  imageUrl,
  caption,
  kind: 'mobile',
});

export const FEATURED_PROJECTS: Project[] = [
  {
    ...EMPTY_DETAIL,
    id: -1000,
    thumbnailUrl: mmWebMain,
    screens: [
      webScreen(mmWebMain, '메인'),
      webScreen(mmWebRecruit, '구인정보'),
      webScreen(mmWebRecruitDetail, '채용공고 상세'),
      webScreen(mmWebMinister, '사역자정보'),
      webScreen(mmWebMinisterDetail, '사역자 상세'),
      webScreen(mmWebAi, 'AI 사역지원'),
      webScreen(mmWebAiWorship, 'AI 예배 콘티'),
      webScreen(mmWebRetreat, '수련회 장소'),
      webScreen(mmWebRetreatCasting, '수련회 강사 섭외'),
      webScreen(mmWebBoard, '게시판'),
      appScreen(mmAppHome, '홈'),
      appScreen(mmAppJobs, '구인정보'),
      appScreen(mmAppJobDetail, '채용공고 상세'),
      appScreen(mmAppJobAlert, '채용 알림 설정'),
      appScreen(mmAppMinisters, '사역자정보'),
      appScreen(mmAppMinisterDetail, '사역자 상세'),
      appScreen(mmAppAi, 'AI 사역지원'),
      appScreen(mmAppAiSongs, '찬양 찾기'),
      appScreen(mmAppAiConti, '예배 콘티'),
      appScreen(mmAppRetreat, '수련회'),
      appScreen(mmAppRetreatPlace, '수련회 장소 상세'),
      appScreen(mmAppRetreatCasting, '강사 섭외'),
      appScreen(mmAppBoard, '게시판'),
    ],
    title: '사역자모아',
    description:
      '교회와 사역자를 연결하는 구인구직·사역 지원 플랫폼. 채용공고, 수련회 장소·강사, AI 설교지원·콘티·찬양 찾기, 커뮤니티까지 한곳에서 제공합니다.',
    category: 'own',
    techStack: ['React', 'React Native', 'Node.js', 'MySQL', 'AWS'],
    projectUrl: 'https://ministermore.co.kr/',
    webUrl: 'https://ministermore.co.kr/',
    androidUrl: 'https://play.google.com/store/apps/details?id=com.ministermore',
    iosUrl: 'https://apps.apple.com/kr/app/%EC%82%AC%EC%97%AD%EC%9E%90%EB%AA%A8%EC%95%84/id6771267063',
    headline: '교회와 사역자를 잇는 사역 지원 플랫폼',
    client: '예플랫 (자체 서비스)',
    industry: '교회 · 사역 지원',
    scope: '기획 · UX/UI · 웹/앱 개발 · 운영',
    platform: 'Web · iOS · Android',
    approach:
      '채용공고, 수련회 장소·강사, AI 설교지원·콘티·찬양 찾기, 커뮤니티를 한 플랫폼에 모았습니다.',
  }
];

export interface PlatformLink {
  key: 'web' | 'android' | 'ios';
  label: string;
  url: string;
}

// 입력된 링크만 웹 → 안드로이드 → 애플 순서로 반환
export const getPlatformLinks = (project: Project): PlatformLink[] =>
  [
    { key: 'web' as const, label: '웹', url: project.webUrl },
    { key: 'android' as const, label: '안드로이드 앱', url: project.androidUrl },
    { key: 'ios' as const, label: '애플 앱', url: project.iosUrl },
  ].filter((link) => link.url);

export const mergeFeaturedProjects = (projects: Project[]): Project[] => {
  const existingTitles = new Set(projects.map((project) => project.title.trim().toLowerCase()));
  const missing = FEATURED_PROJECTS.filter(
    (project) => !existingTitles.has(project.title.trim().toLowerCase())
  );
  return [...missing, ...projects];
};
